import type { FC } from 'react';
import React, { Fragment, useContext, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';

import Spinner from '@atlaskit/spinner/spinner';

import {
	SPACE_DIRECTORY_EXPERIENCE,
	SPACE_DIRECTORY_FILTER_EXPERIENCE,
	SPACE_DIRECTORY_PAGINATE_EXPERIENCE,
	ExperienceStop,
} from '@confluence/experience-tracker';
import { PerformanceEnd, PERFORMANCE_SUBJECT_spaceDirectory } from '@confluence/performance';
import { PageLoadEnd } from '@confluence/browser-metrics';
import { ViewportObserver } from '@confluence/viewport-observer';

import { useSpaceDirectoryData, LoadingState } from '../useSpaceDirectoryData';
import { SPACE_DIRECTORY_PAGE_LOAD } from '../perf.config';
import { FiltersContext } from '../Filters';
import { ErrorMessage } from '../ErrorMessage';
import { TrashMessage } from '../TrashMessage';

import { TableRow } from './TableRow';
import { EmptyState } from './EmptyState';
import {
	TableContainer,
	LoadingOverlay,
	OverlaySpinnerContainer,
	PaginationSpinnerContainer,
} from './PresentationalComponents';

const SCROLL_THRESHOLD = 5;

const useDebouncedState = <T extends {}>(
	initialValue: T,
	wait: number,
): [T, (newValue: T) => void] => {
	const [value, setValue] = useState(initialValue);

	const debouncedSetValue = useMemo(() => debounce(setValue, wait), [wait]);

	return [value, debouncedSetValue];
};

export const Table: FC = () => {
	const { filters } = useContext(FiltersContext);
	const { spaces, loadingState, pageIndex, firstPageError, nextPageError, nextPage } =
		useSpaceDirectoryData({
			filters,
		});

	const [categoriesWidth, setCategoriesWidth] = useDebouncedState(-1, 200);

	const error = nextPageError || firstPageError;

	return (
		<TableContainer>
			{filters.spaceType === 'trashed' ? (
				<TrashMessage />
			) : (
				<Fragment>
					{spaces.length === 0 && loadingState === LoadingState.done && !error ? (
						<EmptyState />
					) : null}
					{spaces.map((space, i) => {
						const key = space.key || i;
						const row = (
							<TableRow
								space={space}
								key={key}
								ranking={i + 1}
								pageNumber={pageIndex + 1}
								textFilter={!!filters.search}
								categoriesWidth={categoriesWidth}
								setCategoriesWidth={i == 0 ? setCategoriesWidth : undefined}
							/>
						);

						// Nth element from bottom scrolling into viewport will trigger page load
						if (spaces.length - i === SCROLL_THRESHOLD) {
							return (
								<ViewportObserver key={`${key}-observer`} onViewportEnter={nextPage}>
									{row}
								</ViewportObserver>
							);
						}

						return row;
					})}

					{error ? <ErrorMessage apolloError={error} /> : null}

					{loadingState === LoadingState.initial ? (
						<Fragment>
							<LoadingOverlay />
							<OverlaySpinnerContainer>
								<Spinner size="large" testId="overlay-spinner" />
							</OverlaySpinnerContainer>
						</Fragment>
					) : loadingState === LoadingState.pagination ? (
						<PaginationSpinnerContainer>
							<Spinner size="large" testId="pagination-spinner" />
						</PaginationSpinnerContainer>
					) : (
						<Fragment>
							<ExperienceStop name={SPACE_DIRECTORY_EXPERIENCE} error={firstPageError} />
							<ExperienceStop name={SPACE_DIRECTORY_FILTER_EXPERIENCE} error={firstPageError} />
							<ExperienceStop name={SPACE_DIRECTORY_PAGINATE_EXPERIENCE} error={nextPageError} />
							<PerformanceEnd subject={PERFORMANCE_SUBJECT_spaceDirectory} subjectId="initial" />
							<PageLoadEnd metric={SPACE_DIRECTORY_PAGE_LOAD} />
						</Fragment>
					)}
				</Fragment>
			)}
		</TableContainer>
	);
};
